import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import LoginForm from '../pages/login/components/LoginForm';
import { ApiService, clearStoresCache, setNoAuthCallback } from '../services/api';
import { checkPermission, transformPermissions } from '../utils/permissions';
import { useNotificationContext } from './NotificationContext';

interface AuthContextValues {
  token: string;
  legacyToken: string;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  user: { id: string; name: string } | null;
  hasInternalAccess: boolean;
}

export const AuthContext = createContext<AuthContextValues>({
  token: '',
  legacyToken: '',
  login: () => Promise.reject(new Error('Not implemented')),
  logout: () => {},
  user: null,
  hasInternalAccess: false,
});

type AuthContextProviderProps = PropsWithChildren<{}>;

type AuthPayload = {
  exp: number;
  id: string;
  name: string;
  new_token: string;
  permissions: string[];
};

const isAuthPayloadValid = (authPayload: unknown): authPayload is AuthPayload => {
  if (typeof authPayload !== 'object' || authPayload === null) {
    return false;
  }
  if (!('exp' in authPayload) || typeof authPayload.exp !== 'number') {
    return false;
  }
  const now = Date.now().valueOf() / 1000;
  return authPayload.exp >= now;
};

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({ children }) => {
  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const [tokenExp, setTokenExp] = useState<number | null>(
    localStorage.getItem('tokenExp') ? Number(localStorage.getItem('tokenExp')) : null
  );
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [legacyToken, setLegacyToken] = useState(localStorage.getItem('legacyToken') || '');

  const user = useMemo(() => {
    try {
      if (!legacyToken) {
        return null;
      }

      const authPayload: AuthPayload = jwtDecode(legacyToken);

      return {
        id: authPayload.id,
        name: authPayload.name,
        permissions: authPayload.permissions,
      };
    } catch {
      return null;
    }
  }, [legacyToken]);

  const persistToken = useCallback((token: string) => {
    try {
      const authPayload = jwtDecode(token);

      if (!isAuthPayloadValid(authPayload)) {
        return;
      }

      setToken(authPayload.new_token);
      localStorage.setItem('token', authPayload.new_token);

      setTokenExp(authPayload.exp);
      localStorage.setItem('tokenExp', authPayload.exp.toString());

      setLegacyToken(token);
      localStorage.setItem('legacyToken', token);
    } catch {}
  }, []);

  const loginToCdn = async (token: string) => {
    try {
      await ApiService.loginCdn(token);
    } catch {}
  };

  useEffect(() => {
    if (legacyToken) {
      loginToCdn(legacyToken);

      const intervalId = setInterval(
        () => {
          loginToCdn(legacyToken);
        },
        30 * 60 * 1000
      ); // 30 minutes

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [legacyToken]);

  const login = useCallback(
    async (username: string, password: string) => {
      toggleLoading(true);
      try {
        const token = await ApiService.login(username, password);
        persistToken(token);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Connection error');
      }
      toggleLoading(false);
    },
    [showErrorToast, toggleLoading, persistToken]
  );

  const logout = useCallback(
    (message = '') => {
      setToken('');
      localStorage.removeItem('token');
      setLegacyToken('');
      localStorage.removeItem('legacyToken');
      setTokenExp(null);
      localStorage.removeItem('tokenExp');
      clearStoresCache();
      if (message) {
        showErrorToast(message);
      } else {
        showSuccessToast('Logged out successfully');
      }
    },
    [showErrorToast, showSuccessToast]
  );

  const extendToken = useCallback(async () => {
    try {
      const token = await ApiService.extendToken(legacyToken);

      persistToken(token);
    } catch {}
  }, [legacyToken, persistToken]);

  useEffect(() => {
    if (!tokenExp) {
      return;
    }

    const checkTokenExpiration = () => {
      const tokenExpirationTime = tokenExp * 1000;
      const diff = tokenExpirationTime - new Date().getTime();

      if (diff < 0) {
        return;
      }

      if (diff < 15 * 1000 * 60) {
        extendToken();
      }
    };

    const intervalId = setInterval(checkTokenExpiration, 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [tokenExp, extendToken]);

  useEffect(() => {
    setNoAuthCallback(() => {
      logout('Session expired, please log in again');
    });
  }, [logout]);

  const permissions = useMemo(() => {
    return transformPermissions(user?.permissions ?? []);
  }, [user]);

  const hasInternalAccess = useMemo(() => {
    return checkPermission('control:*', permissions);
  }, [permissions]);

  return (
    <AuthContext.Provider
      value={{
        token,
        legacyToken,
        login,
        logout,
        user,
        hasInternalAccess,
      }}
    >
      {token ? children : <LoginForm onLogin={login} />}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValues => useContext(AuthContext);
